// src/components/SettingsModal.js
import React, { useState, useEffect } from 'react';
import { auth, db, googleProvider } from '../firebase'; // إضافة googleProvider
import { doc, getDoc, updateDoc, query, where, getDocs, deleteDoc, collection } from 'firebase/firestore';
import { 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  sendPasswordResetEmail, 
  deleteUser,
  reauthenticateWithPopup // إضافة دالة إعادة المصادقة بالنافذة المنبثقة
} from 'firebase/auth';
import './SettingsModal.css';

function SettingsModal({ currentLanguage, getLocalizedText, onClose, initialSection = null }) {
  const [activeSection, setActiveSection] = useState(initialSection || null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // التحقق مما إذا كان المستخدم قد سجل الدخول باستخدام جوجل
  const isGoogleUser = auth.currentUser?.providerData.some(
    (provider) => provider.providerId === 'google.com'
  );

  // تحديث القائمة: إخفاء تغيير كلمة المرور لمستخدمي جوجل
  const menuItems = [
    { key: 'changeName', labelAr: 'تغيير الاسم', labelEn: 'Change Name' },
    ...(!isGoogleUser ? [{ key: 'changePassword', labelAr: 'تغيير كلمة المرور', labelEn: 'Change Password' }] : []),
    { key: 'deleteAccount', labelAr: 'حذف الحساب', labelEn: 'Delete Account' },
  ];

  useEffect(() => {
    // إذا كان القسم نشطاً ومخصصاً لكلمة المرور ولكن المستخدم جوجل، أعد توجيهه
    if (activeSection === 'changePassword' && isGoogleUser) {
      setActiveSection('changeName');
    }

    if (activeSection === 'changeName' && auth.currentUser) {
      const fetchUserData = async () => {
        try {
          const userDocRef = doc(db, 'verified_users', auth.currentUser.uid);
          const userSnap = await getDoc(userDocRef);
          if (userSnap.exists()) {
            const data = userSnap.data();
            setFirstName(data.firstName || '');
            setLastName(data.lastName || '');
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError(getLocalizedText('حدث خطأ أثناء جلب البيانات. حاول مرة أخرى.', 'Error fetching data. Please try again.'));
        }
      };
      fetchUserData();
    }
  }, [activeSection, getLocalizedText, isGoogleUser]);

  useEffect(() => {
    setError('');
    setSuccess('');
    setPassword('');
    setShowPassword(false);
  }, [activeSection]);

  const handleSaveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError(getLocalizedText('يرجى ملء جميع الحقول', 'Please fill all fields'));
      return;
    }

    if (!isGoogleUser && !password.trim()) {
      setError(getLocalizedText('يرجى إدخال كلمة المرور', 'Please enter password'));
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // إعادة التصديق بناءً على نوع حساب المستخدم
      if (isGoogleUser) {
        await reauthenticateWithPopup(auth.currentUser, googleProvider);
      } else {
        const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
        await reauthenticateWithCredential(auth.currentUser, credential);
      }

      // تحديث البيانات في Firestore
      const userDocRef = doc(db, 'verified_users', auth.currentUser.uid);
      await updateDoc(userDocRef, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        fullName: `${firstName.trim()} ${lastName.trim()}`,
        updatedAt: new Date().toISOString(),
      });

      setSuccess(getLocalizedText('تم تحديث الاسم بنجاح', 'Name updated successfully'));
      setPassword(''); 
    } catch (err) {
      let errorMsg = getLocalizedText('حدث خطأ أثناء التحديث. حاول مرة أخرى.', 'An error occurred during update. Please try again.');
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errorMsg = getLocalizedText('كلمة المرور غير صحيحة', 'Incorrect password');
      } else if (err.code === 'auth/too-many-requests') {
        errorMsg = getLocalizedText('محاولات كثيرة، حاول لاحقاً', 'Too many attempts, try later');
      } else if (err.code === 'auth/popup-closed-by-user') {
        errorMsg = getLocalizedText('تم إلغاء عملية التحقق', 'Verification was cancelled');
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetLink = async () => {
    if (!auth.currentUser?.email) {
      setError(getLocalizedText('لا يوجد بريد إلكتروني مرتبط بالحساب', 'No email associated with this account'));
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await sendPasswordResetEmail(auth, auth.currentUser.email);
      
      setSuccess(getLocalizedText(
        'تم إرسال رابط إعادة تعيين كلمة المرور بنجاح. تحقق من بريدك الإلكتروني وكذلك مجلد السبام.',
        'Password reset link sent successfully. Check your email and spam folder.'
      ));
    } catch (err) {
      let errorMsg = getLocalizedText(
        'حدث خطأ أثناء إرسال الرابط. حاول مرة أخرى.',
        'An error occurred while sending the link. Please try again.'
      );

      if (err.code === 'auth/too-many-requests') {
        errorMsg = getLocalizedText(
          'تم إرسال طلبات كثيرة جدًا. حاول مرة أخرى بعد قليل.',
          'Too many requests. Please try again later.'
        );
      }

      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const maskEmail = (email) => {
    if (!email) return '';
    
    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    const maskedLocal = localPart.length > 3
      ? localPart.slice(0, 3) + '*'.repeat(Math.max(localPart.length - 4, 3)) + localPart.slice(-1)
      : localPart.slice(0, 1) + '*'.repeat(localPart.length - 1);

    const [domainName, domainExt] = domain.split('.');
    const maskedDomain = domainName.length > 1
      ? domainName.slice(0, 1) + '*'.repeat(domainName.length - 1)
      : '*'.repeat(domainName.length);

    return `${maskedLocal}@${maskedDomain}.${domainExt || ''}`;
  };

  const handleDeleteAccount = async () => {
    if (!isGoogleUser && !password.trim()) {
      setError(getLocalizedText('يرجى إدخال كلمة المرور', 'Please enter password'));
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // إعادة التصديق بناءً على نوع حساب المستخدم
      if (isGoogleUser) {
        await reauthenticateWithPopup(auth.currentUser, googleProvider);
      } else {
        const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
        await reauthenticateWithCredential(auth.currentUser, credential);
      }

      // حذف جميع الإعلانات المنشورة من قبل المستخدم
      const adsQuery = query(collection(db, 'ads'), where('userId', '==', auth.currentUser.uid));
      const adsSnapshot = await getDocs(adsQuery);
      const deleteAdsPromises = adsSnapshot.docs.map((adDoc) => deleteDoc(adDoc.ref));
      await Promise.all(deleteAdsPromises);

      // حذف إعلانات profile_ads أيضًا
      const profileAdsQuery = query(collection(db, 'profile_ads'), where('userId', '==', auth.currentUser.uid));
      const profileAdsSnapshot = await getDocs(profileAdsQuery);
      const deleteProfileAdsPromises = profileAdsSnapshot.docs.map((adDoc) => deleteDoc(adDoc.ref));
      await Promise.all(deleteProfileAdsPromises);

      // حذف وثيقة المستخدم من Firestore
      const userDocRef = doc(db, 'verified_users', auth.currentUser.uid);
      await deleteDoc(userDocRef);

      // حذف المستخدم من Firebase Authentication
      await deleteUser(auth.currentUser);

      setSuccess(getLocalizedText('تم حذف الحساب بنجاح', 'Account deleted successfully'));
      setPassword(''); 

      setTimeout(() => {
        onClose();
        window.location.href = '/'; 
      }, 2000);
    } catch (err) {
      let errorMsg = getLocalizedText('حدث خطأ أثناء حذف الحساب. حاول مرة أخرى.', 'An error occurred during account deletion. Please try again.');
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        errorMsg = getLocalizedText('كلمة المرور غير صحيحة', 'Incorrect password');
      } else if (err.code === 'auth/too-many-requests') {
        errorMsg = getLocalizedText('محاولات كثيرة، حاول لاحقاً', 'Too many attempts, try later');
      } else if (err.code === 'auth/requires-recent-login') {
        errorMsg = getLocalizedText('يرجى تسجيل الدخول مرة أخرى لإكمال العملية', 'Please log in again to complete the process');
      } else if (err.code === 'auth/popup-closed-by-user') {
        errorMsg = getLocalizedText('تم إلغاء عملية التحقق، لا يمكن إتمام الحذف', 'Verification cancelled, cannot delete account');
      }
      setError(errorMsg);
      console.error('Error deleting account:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h2>{getLocalizedText('الإعدادات', 'Settings')}</h2>
          <span className="settings-close-button" onClick={onClose}>×</span>
        </div>

        <div className="settings-modal-body">
          <div className={`settings-menu ${currentLanguage === 'ar' ? 'ar' : 'en'}`}>
            {menuItems.map(item => (
              <div
                key={item.key}
                className={`settings-menu-item ${activeSection === item.key ? 'active' : ''}`}
                onClick={() => setActiveSection(item.key)}
              >
                {getLocalizedText(item.labelAr, item.labelEn)}
              </div>
            ))}
          </div>

          <div className="settings-content-area">
            {activeSection === null && (
              <p className="placeholder-text">
                {getLocalizedText('اختر خيارًا من القائمة للبدء', 'Select an option from the menu to begin')}
              </p>
            )}
            {activeSection === 'changeName' && (
              <div className="change-name-section">
                <h3>{getLocalizedText('تغيير الاسم', 'Change Name')}</h3>
                <div className="name-fields">
                  <div className="settings-field">
                    <label>{getLocalizedText('الاسم الأول', 'First Name')}</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={getLocalizedText('أدخل الاسم الأول', 'Enter first name')}
                      dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div className="settings-field">
                    <label>{getLocalizedText('الاسم الأخير', 'Last Name')}</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder={getLocalizedText('أدخل الاسم الأخير', 'Enter last name')}
                      dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>
                
                {/* إظهار حقل كلمة المرور فقط إذا لم يكن مستخدم جوجل */}
                {!isGoogleUser && (
                  <>
                    <div className="settings-field">
                      <label>{getLocalizedText('كلمة المرور', 'Password')}</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={getLocalizedText('أدخل كلمة المرور الحالية', 'Enter current password')}
                        dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                      />
                    </div>
                    <div className="settings-password-toggle-container">
                      <label className="settings-password-toggle-label">
                        <input
                          type="checkbox"
                          checked={showPassword}
                          onChange={(e) => setShowPassword(e.target.checked)}
                        />
                        <span className="settings-password-toggle-text">
                          {getLocalizedText('إظهار كلمة المرور', 'Show password')}
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {error && <div className="settings-error">{error}</div>}
                {success && <div className="settings-success">{success}</div>}
                <button
                  className="settings-save-btn"
                  onClick={handleSaveName}
                  disabled={isLoading}
                >
                  {isLoading ? getLocalizedText('جاري الحفظ...', 'Saving...') : getLocalizedText('حفظ', 'Save')}
                </button>
              </div>
            )}
            
            {activeSection === 'changePassword' && !isGoogleUser && (
              <div className="change-password-section">
                <h3>{getLocalizedText('تغيير كلمة المرور', 'Change Password')}</h3>
    
                <p className="change-password-description">
                  {getLocalizedText(
                    `سنرسل رابط إعادة تعيين كلمة المرور إلى ${maskEmail(auth.currentUser?.email || '')}\nستجده في صندوق الوارد أو في مجلد السبام .`,
                    `We will send a password reset link to ${maskEmail(auth.currentUser?.email || '')}\nand you will find it in your inbox or spam folder.`
                  )}
                </p>

               {error && <div className="settings-error">{error}</div>}
               {success && <div className="settings-success">{success}</div>}

               <button
                 className="settings-save-btn change-password-send-btn"
                 onClick={handleSendResetLink}
                 disabled={isLoading}
               >
                 {isLoading 
                   ? getLocalizedText('جاري الإرسال...', 'Sending...') 
                   : getLocalizedText('إرسال', 'Send')
                 }
                </button>
              </div>
            )}

            {activeSection === 'deleteAccount' && (
              <div className="delete-account-section">
                <h3>{getLocalizedText('حذف الحساب', 'Delete Account')}</h3>
                <p className="delete-warning">
                  {getLocalizedText(
                    'إذا قمت بحذف الحساب، سيتم حذف جميع الإعلانات المنشورة أيضًا.',
                    'If you delete the account, all posted ads will be deleted as well.'
                  )}
                </p>

                {/* إظهار حقل كلمة المرور فقط إذا لم يكن مستخدم جوجل */}
                {!isGoogleUser && (
                  <>
                    <div className="settings-field">
                      <label>{getLocalizedText('كلمة المرور', 'Password')}</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={getLocalizedText('أدخل كلمة المرور الحالية', 'Enter current password')}
                        dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                      />
                    </div>
                    <div className="settings-password-toggle-container">
                      <label className="settings-password-toggle-label">
                        <input
                          type="checkbox"
                          checked={showPassword}
                          onChange={(e) => setShowPassword(e.target.checked)}
                        />
                        <span className="settings-password-toggle-text">
                          {getLocalizedText('إظهار كلمة المرور', 'Show password')}
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {error && <div className="settings-error">{error}</div>}
                {success && <div className="settings-success">{success}</div>}
                <button
                  className="settings-save-btn settings-delete-btn"
                  onClick={handleDeleteAccount}
                  disabled={isLoading}
                >
                  {isLoading ? getLocalizedText('جاري الحذف...', 'Deleting...') : getLocalizedText('حذف الحساب', 'Delete Account')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;